import React, { useEffect, useState } from 'react';
import {
  FlexStyle, Pressable, StyleSheet, Text, TouchableOpacity, View,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import Moment from 'moment';
import { GestureHandlerRootView, ScrollView } from 'react-native-gesture-handler';
import Animated, {
  useAnimatedStyle, useSharedValue, withRepeat, withSequence, withSpring,
} from 'react-native-reanimated';
import Colors from '../../constants/Colors';
import CompareArrowsIcon from '../CompareArrowsIcon';
import CalendarPicker from '../../CalendarPicker';
import NewSwitch from '../NewSwitch';
import { logEvent } from '../LogEvent';
import { DateSelectionRange } from '../../redux/dateRangeSlice';
import RangeButton from './components/RangeButton';
import CalendarIconButton from './components/CalendarIconButton';
import PressableStyled, { PressableState } from '../PressableStyled';
import CompareButton from './components/CompareButton';
import Button from '../Button';
import isMobile from '../IsMobile';

interface Props {
  // eslint-disable-next-line no-unused-vars
  onChange: (start: string, end: string, selectedRange?: DateSelectionRange) => void;
  // eslint-disable-next-line no-unused-vars
  onCompareChange?: (start: string, end: string, selectedRange?: DateSelectionRange) => void;
  // eslint-disable-next-line no-unused-vars
  onConfirm: () => void;
  // eslint-disable-next-line no-unused-vars
  onOpen: (open: any) => void;
  startDate: any;
  compareStartDate?: any;
  endDate: any;
  compareEndDate?: any;
  compareEnabled?: boolean;
  selectedRange?: DateSelectionRange;
  // eslint-disable-next-line no-unused-vars
  setSelectedRange?: (range: any) => void;
  renderLeft?: boolean;
  testID?: string;
  liveEnabled?: boolean;
  hideCalendarIcon?: boolean;
  style?: FlexStyle,
  smartGuard?: boolean;
}

export default function DateComponent(props: Props) {
  const [selectedRangeIndex, setSelectedRangeIndex] = useState<DateSelectionRange>(props
    .selectedRange ?? 1);
  const daySelected: boolean = selectedRangeIndex === 0;
  const weekSelected: boolean = selectedRangeIndex === 1;
  const monthSelected: boolean = selectedRangeIndex === 2;
  const threeMonthsSelected: boolean = selectedRangeIndex === 3;
  const sixMonthsSelected: boolean = selectedRangeIndex === 4;
  const yearSelected: boolean = selectedRangeIndex === 5;
  const liveSelected: boolean = selectedRangeIndex === 7;
  const [dateSelected, setDateSelected] = useState<boolean>(false);
  const [compareToggled, setCompareToggled] = useState<boolean>(props.compareStartDate
    && props.compareEndDate);
  const [selectingCompare, setSelectingCompare] = useState<boolean>(false);
  const [compareApplied, setCompareApplied] = useState<boolean>(props.compareStartDate
    && props.compareEndDate);

  const [startDateToApply, setStartDateToApply] = useState(props.startDate);
  const [endDateToApply, setEndDateToApply] = useState(props.endDate);

  const [compareStartDateToApply, setCompareStartDateToApply] = useState(
    props.compareStartDate ?? new Date(),
  );
  const [compareEndDateToApply, setCompareEndDateToApply] = useState(
    props.compareEndDate ?? new Date(),
  );
  const comparing: boolean = compareToggled && selectingCompare;

  useEffect(() => setSelectedRangeIndex(props.selectedRange ?? 1), [props.selectedRange]);

  useEffect(() => {
    if (props.setSelectedRange) props.setSelectedRange(selectedRangeIndex);
  }, [selectedRangeIndex]);

  useEffect(() => setStartDateToApply(props.startDate), [props.startDate]);

  useEffect(() => setEndDateToApply(props.endDate), [props.endDate]);

  const navigation = useNavigation();
  navigation.addListener('blur', () => setDateSelected(false));

  const pulseOpacity = useSharedValue<number>(1);

  const pulseAnimation = useAnimatedStyle(() => ({
    opacity: pulseOpacity.value,
  }));

  useEffect(() => {
    pulseOpacity.value = withRepeat(withSequence(
      withSpring(0.3),
      withSpring(1),
    ), 0);
  }, []);

  const renderRangePickers = () => (
    <View style={{ flexDirection: isMobile ? 'row' : 'column' }}>
      {props.liveEnabled && <RangeButton
        {...{
          selectedRangeIndex,
          setSelectedRangeIndex,
          setStartDateToApply,
          setEndDateToApply,
          setCompareStartDateToApply,
          setCompareEndDateToApply,
          comparing,
          compareToggled,
          index: 7,
          title: 'LIVE',
        }}
      />}
      {(isMobile
        ? ['1D', '1W', '1M', '3M', '6M', 'Y', 'CUSTOM']
        : ['DAY', 'LAST WEEK', 'LAST MONTH', 'LAST 3 MONTHS', 'LAST 6 MONTHS', 'LAST YEAR', 'CUSTOM'])
        .map((title: string, index: DateSelectionRange) => <RangeButton
          {...{
            selectedRangeIndex,
            setSelectedRangeIndex,
            setStartDateToApply,
            setEndDateToApply,
            setCompareStartDateToApply,
            setCompareEndDateToApply,
            comparing,
            compareToggled,
            index,
            title,
          }}
          key={`range_button_${title}`}
        />)}
    </View>
  );

  const renderCalendar = () => (
    <CalendarPicker
      weekdays={['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']}
      dayLabelsWrapper={[
        styles.dayLabelsWrapper,
        comparing && { borderColor: Colors.light.hereworksYellow500 },
      ]}
      disabledDatesTextStyle={styles.disabledDatesTextStyle}
      dayLabelsStyle={styles.dayLabel}
      todayBorderColor={comparing ? Colors.light.hereworksYellow600 : '#548FC8'}
      restrictMonthNavigation
      headerWrapperStyle={styles.rangeSelectionControlRow}
      monthYearHeaderWrapperStyle={styles.rangeSelectionHeader}
      titleStyle={styles.rangeSelectionTitleStyle}
      headerButtonStyle={{
        color: comparing ? Colors.light.hereworksYellow500 : Colors.light.hereworksBlue500,
      }}
      onDateChange={(change, type) => {
        let newStart = null;
        let newEnd = null;
        if (sixMonthsSelected) {
          if (type === 'START_DATE') {
            newStart = Moment(change).subtract(6, 'months').startOf('day').toISOString();
            newEnd = change.toISOString();
          }
        } else if (threeMonthsSelected) {
          if (type === 'START_DATE') {
            newStart = Moment(change).subtract(3, 'months').startOf('day').toISOString();
            newEnd = change.toISOString();
          }
        } else if (monthSelected) {
          if (type === 'START_DATE') {
            newStart = Moment(change).subtract(1, 'month').startOf('day').toISOString();
            newEnd = change.toISOString();
          }
        } else if (weekSelected) {
          if (type === 'START_DATE') {
            newStart = Moment(change).subtract(6, 'days').startOf('day').toISOString();
            newEnd = change.toISOString();
          }
        } else if (yearSelected) {
          if (type === 'START_DATE') {
            newStart = Moment(change).subtract(365, 'days').startOf('day').toISOString();
            newEnd = change.toISOString();
          }
        } else if (daySelected) {
          newStart = Moment(change).startOf('day').toISOString();
          newEnd = Moment(change).endOf('day').toISOString();
        } else if (change && type === 'END_DATE') {
          newStart = selectingCompare ? compareStartDateToApply : startDateToApply;
          newEnd = change.toISOString();
        } else if (change) {
          newStart = Moment(change).startOf('day').toISOString();
          newEnd = null;
        }

        if (selectingCompare && change) {
          setCompareStartDateToApply(newStart);
          setCompareEndDateToApply(newEnd);
        } else if (change) {
          setStartDateToApply(newStart);
          setEndDateToApply(newEnd);
        }
      }}
      allowRangeSelection={!daySelected && !liveSelected}
      textStyle={{
        color: Colors.light.hereworksDarkBlue900,
        fontFamily: 'Roboto_500Medium',
        fontSize: 14,
        lineHeight: 15,
      }}
      width={300}
      height={319}
      selectedStartDate={selectingCompare ? compareStartDateToApply : startDateToApply}
      selectedEndDate={selectingCompare ? compareEndDateToApply : endDateToApply}
      selectedDayStyle={{
        backgroundColor: selectingCompare
          ? Colors.light.hereworksYellow500
          : Colors.light.hereworksLightBlue,
      }}
      selectedRangeStartStyle={{
        backgroundColor: selectingCompare
          ? Colors.light.hereworksYellow500
          : Colors.light.hereworksLightBlue,
      }}
      selectedRangeEndStyle={{
        backgroundColor: selectingCompare
          ? Colors.light.hereworksYellow500
          : Colors.light.hereworksLightBlue,
      }}
      selectedDayTextStyle={{
        color: [0, 7].includes(selectedRangeIndex) ? '#FFF' : '#000',
        fontFamily: 'Roboto_500Medium',
        fontSize: 14,
        lineHeight: 32,
      }}
      selectedRangeStartTextStyle={{
        color: '#fff',
        fontFamily: 'Roboto_500Medium',
        fontSize: 14,
        lineHeight: 32,
      }}
      selectedRangeEndTextStyle={{
        color: '#fff',
        fontFamily: 'Roboto_500Medium',
        fontSize: 14,
        lineHeight: 32,
      }}
      selectedRangeStyle={{
        backgroundColor: selectingCompare
          ? Colors.light.hereworksYellow700
          : Colors.light.hereworksBlue50,
      }}
      maxDate={Date.now()}
      minDate={Moment('2021-01-01').valueOf()}
      initialDate={props.endDate ?? undefined}
    />
  );

  const renderApplyCancel = () => {
    const applyDisabled = (!startDateToApply || !endDateToApply) || (compareToggled
      && (!compareStartDateToApply || !compareEndDateToApply));
    return <View style={{ flexDirection: 'row' }}>
      <Button
        onPress={() => {
          props.onOpen(!dateSelected);
          setDateSelected(!dateSelected);
          setStartDateToApply(props.startDate);
          setEndDateToApply(props.endDate);
          setCompareStartDateToApply(props.compareStartDate);
          setCompareEndDateToApply(props.compareEndDate);
          setCompareToggled(compareApplied);
          setSelectingCompare(compareApplied);
          logEvent('DateComponent', 'Cancel');
        }}
        title='Cancel'
        style={styles.button}
        textStyle={styles.buttonTitle}
        testID='cancel-btn'
      />
      <Button
        affirmative
        onPress={() => {
          props.onChange(startDateToApply, endDateToApply, selectedRangeIndex);
          if (compareToggled) props.onCompareChange(compareStartDateToApply, compareEndDateToApply);
          else props.onCompareChange(null, null);
          setCompareApplied(compareToggled);
          props.onConfirm();
          props.onOpen(!dateSelected);
          setDateSelected(!dateSelected);
          logEvent('DateComponent', 'Apply');
        }}
        title='Apply'
        style={styles.button}
        textStyle={styles.buttonTitle}
        disabled={applyDisabled}
        testID='ticket-date-picker-apply-btn'
      />
    </View>;
  };

  const renderDateText = (startDate: string, endDate: string) => {
    if (liveSelected) {
      return <View style={{
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1,
        justifyContent: 'center',
      }}>
        <Text style={[styles.liveText, { color: Colors.light.hereworksBlack400 }]}>
          LIVE
        </Text>
        <Animated.View style={[styles.liveCircle, pulseAnimation]} />
      </View>;
    }

    if (daySelected) {
      return (
        <Text style={[styles.dateText, { color: Colors.light.hereworksBlack600 }]}>
          {Moment(startDate ?? new Date()).format('D MMM YYYY')}
        </Text>
      );
    }

    if (!endDate || !startDate) {
      return <Text
        style={[styles.dateText, { color: Colors.light.hereworksBlack600 }]}
        testID='choose-date-btn'
      >
        {'Choose date'}
      </Text>;
    }

    return (
      <Text style={[styles.dateText, { color: Colors.light.hereworksBlack600 }]}>
        {Moment(startDate ?? new Date()).format('D MMM YYYY')} -{' '}
        {Moment(endDate ?? new Date()).format('D MMM YYYY')}
      </Text>
    );
  };

  const renderCompareToggleButton = () => (
    <>
      {props.compareEnabled && (isMobile ? <PressableStyled
        style={styles.compareDatesButton}
        onPress={() => setCompareToggled(!compareToggled)}
      >
        <CompareArrowsIcon />
        <Text style={styles.compareDatesText}>
          {compareToggled ? 'Turn off date comparison' : 'Compare dates'}
        </Text>
      </PressableStyled>
        : <View style={styles.compareToggleContainer}>
          <Text style={styles.compareToggleLabel}>
            {compareToggled ? 'COMPARE ON' : 'COMPARE OFF'}
          </Text>
          <Pressable
            style={({ hovered, pressed }: PressableState) => [{
              opacity: hovered || pressed ? 0.5 : 1,
            }]}
            testID='sa-compare-on-off-toggle-btn'
            onPress={() => {
              if (compareToggled) {
                setCompareStartDateToApply(null);
                setCompareEndDateToApply(null);
                setSelectingCompare(false);
              } else {
                setSelectingCompare(true);
              }
              setCompareToggled(!compareToggled);
              logEvent('DateComponent', compareToggled ? 'Close_comparison' : 'Compare_dates');
            }}
          >
            <NewSwitch
              isToggled={compareToggled}
              color={
                selectingCompare ? Colors.light.hereworksYellow500 : Colors.light.hereworksBlue500
              }
              style={null}
            />
          </Pressable>
        </View>
      )}
    </>
  );

  const renderFirstDate = () => (
    <View style={styles.firstDateDisplay}>
      {compareToggled && (
        <Text
          style={{
            color: Colors.light.hereworksPrimaryGrey700,
            fontFamily: 'Roboto_500Medium',
            fontSize: 12,
            marginBottom: 4,
          }}
        >
          Date 1
        </Text>
      )}
      <TouchableOpacity
        style={{
          height: 40,
          borderWidth: 1.5,
          borderColor: selectingCompare ? Colors.light.hereworksBlue100
            : Colors.light.hereworksBlue500,
          borderRadius: 10,
          marginTop: 4,
        }}
        onPress={() => {
          if (selectingCompare) {
            setSelectingCompare(false);
          }
          logEvent('DateComponent', 'First_date');
        }}
        testID='date1-picker-btn'
      >
        {renderDateText(startDateToApply, endDateToApply)}
      </TouchableOpacity>
    </View>
  );

  const renderCompareDate = () => (
    <>
      {compareToggled && (
        <View style={styles.comparisonDateDisplay}>
          <Text
            style={{
              color: Colors.light.hereworksPrimaryGrey700,
              fontFamily: 'Roboto_500Medium',
              fontSize: 12,
              marginBottom: 4,
            }}
          >
            Date 2
          </Text>
          <TouchableOpacity
            style={{
              height: 40,
              borderWidth: 1.5,
              borderColor: selectingCompare
                ? Colors.light.hereworksYellow500
                : Colors.light.hereworksYellow700,
              borderRadius: 10,
              marginTop: 4,
            }}
            onPress={() => {
              setSelectingCompare(true);
              if (props.compareStartDate) {
                setCompareStartDateToApply(Moment(props.compareStartDate));
                setCompareEndDateToApply(Moment(props.compareEndDate ?? props.compareStartDate));
              }
              logEvent('DateComponent', 'Compare_date');
            }}
            testID='date2-picker-btn'
          >
            {renderDateText(compareStartDateToApply, compareEndDateToApply)}
          </TouchableOpacity>
        </View>
      )}
    </>
  );

  if (!isMobile) {
    return (
      <View
        style={{ backgroundColor: 'transparent' }}
        testID={props.testID}
      >
        <View style={{ flexDirection: 'row' }}>
          <PressableStyled
            style={{
              flex: 1,
              flexDirection: 'row',
              alignItems: 'center',
              borderWidth: 1.5,
              borderColor: dateSelected
                ? Colors.light.hereworksBlue500 : Colors.light.hereworksBlue100,
              borderRadius: 10,
              backgroundColor: '#fff',
            }}
            onPress={() => {
              props.onOpen(!dateSelected);
              setDateSelected(!dateSelected);
              logEvent('DateComponent', 'First_date_desktop');
            }}
            disabled={props.smartGuard}
            maintainOpacityWhenDisabled
          >
            {renderDateText(startDateToApply, endDateToApply)}
          </PressableStyled>
          {compareToggled && (
            <>
              <View style={{ marginLeft: 14, justifyContent: 'center' }}>
                <CompareArrowsIcon />
              </View>
              <TouchableOpacity
                style={{
                  borderWidth: 1.5,
                  borderColor: dateSelected
                    ? Colors.light.hereworksLightBlue
                    : Colors.light.hereworksPaleBlue900,
                  borderRadius: 10,
                  marginLeft: 12,
                  backgroundColor: '#fff',
                }}
                onPress={() => {
                  props.onOpen(!dateSelected);
                  setDateSelected(!dateSelected);
                  logEvent('DateComponent', 'Compare_date_desktop');
                }}
              >
                {renderDateText(props.compareStartDate, props.compareEndDate)}
              </TouchableOpacity>
            </>
          )}
        </View>
        {dateSelected && (
          <View style={[
            styles.dateSelectorContainer, props.renderLeft && styles.dateSelectorRenderLeft,
          ]}>
            <View
              style={{
                borderWidth: 1.5,
                borderColor: Colors.light.hereworksLightBlue,
                backgroundColor: '#fff',
                borderRadius: 16,
                flexDirection: 'row',
                zIndex: 1,
                marginBottom: 24,
                flex: 1,
              }}
            >
              <View style={{ borderRightWidth: 1, borderColor: Colors.light.hereworksWhite500 }}>
                <View
                  style={{
                    backgroundColor: 'transparent',
                    borderBottomWidth: 1,
                    borderColor: Colors.light.hereworksWhite500,
                    marginBottom: 10,
                  }}
                >
                  <Text
                    style={{
                      color: Colors.light.hereworksBlack500,
                      fontFamily: 'Roboto_500Medium',
                      fontSize: 12,
                      marginLeft: 24,
                      marginTop: 24,
                      marginBottom: 12,
                    }}
                  >
                    RANGE
                  </Text>
                </View>
                <View style={styles.rangeButtonContainerWeb}>
                  {renderRangePickers()}
                </View>
                {renderCompareToggleButton()}
              </View>
              <View
                style={{ flex: 1 }}
                testID='calendar-dates'
              >
                {renderFirstDate()}
                {renderCompareDate()}
                {renderCalendar()}
                <View
                  style={{
                    marginHorizontal: 24,
                    marginTop: 16,
                    marginBottom: 24,
                    height: 36,
                    justifyContent: 'flex-end',
                    flex: 1,
                  }}
                >
                  {renderApplyCancel()}
                </View>
              </View>
            </View>
          </View>
        )}
      </View>
    );
  }

  // Render Mobile
  return (
    <GestureHandlerRootView>
      <View
        style={{ backgroundColor: 'transparent', marginTop: 10 }}
        testID={props.testID}
      // contentContainerStyle={{ paddingBottom: dateSelected ? 50 : 4 }}
      >
        <View style={{ flexDirection: 'row' }}>
          {compareToggled ? <CompareButton
            {...{
              dateSelected, setDateSelected, logEvent, comparing,
            }}
            onOpen={props.onOpen}
          /> : !props.hideCalendarIcon && <CalendarIconButton
            {...{ dateSelected, setDateSelected, logEvent }}
            onOpen={props.onOpen}
          />}
          <TouchableOpacity
            style={[
              styles.dateFilter,
              {
                flex: 1,
                marginLeft: 8,
                // marginEnd: props.hideCalendarIcon ? 8 : undefined,
                borderColor: dateSelected
                  ? Colors.light.hereworksLightBlue
                  : Colors.light.hereworksBlue100,
              },
              props.style,
            ]}
            onPress={() => {
              if (comparing && dateSelected) setSelectingCompare(false);
              else {
                setSelectingCompare(false);
                props.onOpen(!dateSelected);
                setDateSelected(!dateSelected);
              }
              logEvent('DateComponent', 'First_date_mobile');
            }}
          >
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              {renderDateText(startDateToApply, endDateToApply)}
            </View>
          </TouchableOpacity>
        </View>
        {compareToggled && (
          <TouchableOpacity
            style={[
              styles.dateFilter,
              {
                alignSelf: 'stretch',
                marginTop: 4,
                marginStart: 48,
                borderColor: dateSelected
                  ? Colors.light.hereworksYellow500
                  : Colors.light.hereworksYellow700,
              },
            ]}
            onPress={() => {
              if (!comparing && dateSelected) setSelectingCompare(true);
              else {
                setSelectingCompare(true);
                props.onOpen(!dateSelected);
                setDateSelected(!dateSelected);
              }
              logEvent('DateComponent', 'Compare_date_mobile');
            }}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              {renderDateText(props.compareStartDate, props.compareEndDate)}
            </View>
          </TouchableOpacity>
        )}
        {dateSelected && (
          <ScrollView
            style={styles.subContainer}
            contentContainerStyle={styles.subContainerContent}
          >
            {compareToggled && <>
              {renderFirstDate()}
              {renderCompareDate()}
            </>}
            <View style={{ maxWidth: 314, alignSelf: 'center' }}>
              {renderCalendar()}
            </View>
            {renderCompareToggleButton()}
            <ScrollView
              style={[
                styles.rangeButtonContainerMobile,
                comparing && { borderColor: Colors.light.hereworksYellow700 },
              ]}
              horizontal
              showsHorizontalScrollIndicator={false}
            >
              {renderRangePickers()}
            </ScrollView>
            <View style={styles.buttonContainer}>
              {renderApplyCancel()}
            </View>
          </ScrollView>
        )}
      </View>
    </GestureHandlerRootView>
  );
}

const styles = StyleSheet.create({
  dateFilter: {
    backgroundColor: '#fff',
    alignItems: 'center',
    borderRadius: 6,
    height: 40,
    borderWidth: 1.5,
  },
  dateText: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    marginHorizontal: 30,
    marginVertical: 8,
    textAlign: 'center',
    flex: 1,
  },
  liveText: {
    marginHorizontal: 18,
    marginVertical: 12,
    marginLeft: 12,
    marginRight: 4,
    fontFamily: 'Roboto_400Regular',
    fontSize: 11,
    lineHeight: 11,
    textAlign: 'center',
  },
  dateSelectorContainer: {
    position: 'absolute',
    top: 52,
    marginBottom: 24,
  },
  dateSelectorRenderLeft: {
    end: 0,
  },
  liveCircle: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: Colors.light.hereworksRed500,
    borderWidth: 3,
    borderColor: Colors.light.hereworksLightPink,
    marginRight: 12,
  },
  subContainer: {
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksLightBlue,
    backgroundColor: '#fff',
    marginTop: 12,
    paddingTop: 12,
    borderRadius: 8,
  },
  subContainerContent: {
    paddingBottom: 12,
  },
  buttonContainer: {
    marginHorizontal: 18,
    marginBottom: 24,
    marginTop: 16,
  },
  dayLabelsWrapper: {
    height: 32,
    borderTopWidth: isMobile ? 1 : 0,
    borderBottomWidth: isMobile ? 0 : 1,
    borderColor: isMobile ? Colors.light.hereworksBlue500 : Colors.light.hereworksWhite500,
    marginBottom: isMobile ? undefined : 4,
    marginHorizontal: 18,
    width: '90%',
  },
  dayLabel: {
    fontSize: 10,
    fontFamily: 'Roboto_500Medium',
    color: Colors.light.hereworksBlack400,
    lineHeight: 15,
  },
  button: {
    flex: 1,
    height: isMobile ? 40 : 36,
  },
  buttonTitle: {
    fontSize: isMobile ? 16 : 12,
    lineHeight: isMobile ? 24 : 20,
    fontFamily: isMobile ? 'Roboto_400Regular' : 'Roboto_500Medium',
  },
  compareToggleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: isMobile ? undefined : 16,
    borderTopWidth: isMobile ? undefined : 1,
    borderTopColor: Colors.light.hereworksWhite500,
    marginTop: 12,
  },
  compareToggleLabel: {
    color: Colors.light.hereworksBlack400,
    fontFamily: 'Roboto_400Regular',
    fontSize: 11,
    lineHeight: 13,
    letterSpacing: 0.33,
    marginStart: 24,
    marginEnd: 12,
  },
  rangeSelectionControlRow: {
    paddingHorizontal: isMobile ? 18 : 24,
  },
  rangeSelectionHeader: {
    paddingHorizontal: isMobile ? undefined : 12,
    justifyContent: isMobile ? 'flex-start' : 'center',
    alignItems: 'center',
    borderRadius: 10,
    borderWidth: isMobile ? undefined : 1.5,
    borderColor: Colors.light.hereworksBlue100,
    height: isMobile ? 21 : 32,
  },
  rangeSelectionTitleStyle: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack600,
    letterSpacing: 0.1,
    paddingEnd: 10,
  },
  rangeButtonContainerWeb: {
    borderRadius: 8,
    marginHorizontal: 24,
    justifyContent: 'space-between',
  },
  rangeButtonContainerMobile: {
    borderWidth: 1.5,
    borderRadius: 8,
    borderColor: Colors.light.hereworksBlue100,
    marginHorizontal: 18,
    marginTop: 16,
  },
  firstDateDisplay: {
    marginBottom: 16,
    marginHorizontal: 16,
    marginTop: 24,
  },
  comparisonDateDisplay: {
    marginBottom: 12,
    marginHorizontal: 16,
  },
  compareDatesButton: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 18,
    marginTop: 12,
  },
  compareDatesText: {
    fontSize: 12,
    lineHeight: 20,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlue500,
    marginStart: 8,
  },
  disabledDatesTextStyle: {
    fontSize: 15,
  },
});
